import { Link } from "react-router-dom";
import { Wrench, Tag, Gauge, Trophy, Handshake } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const money = (v, c = "USD") => {
  if (v == null) return "";
  const sym = c === "CAD" ? "CA$" : "$";
  return `${sym}${Number(v).toLocaleString()}`;
};

function IconButton({ icon: Icon, label, testId, children }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={label}
          data-testid={testId}
          className="flex h-7 w-7 items-center justify-center rounded-full border border-border bg-secondary/60 text-muted-foreground transition-colors hover:border-primary/60 hover:text-primary"
        >
          <Icon className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 border-border bg-card p-3" align="start">
        <p className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
        {children}
      </PopoverContent>
    </Popover>
  );
}

/**
 * Up-to-5 clickable identity icons shown next to a forum poster's name.
 * `profile` is the per-user object from forumProfileAPI.batch().
 */
export default function ForumUserIcons({ profile, className = "" }) {
  if (!profile || !profile.badges) return null;
  const b = profile.badges;
  const hasAny = b.setup || b.listings || b.simhub || b.sponsors || b.achievements;
  if (!hasAny) return null;

  return (
    <div className={`flex items-center gap-1.5 ${className}`} data-testid="forum-user-icons">
      {b.setup && profile.setup && (
        <IconButton icon={Wrench} label="Rig Setup" testId="forum-icon-setup">
          <ul className="space-y-1 text-sm">
            {Object.entries({
              "Wheel Base": profile.setup.wheel_base,
              "Wheel": profile.setup.steering_wheel,
              "Pedals": profile.setup.pedals,
              "Cockpit": profile.setup.cockpit,
              "Motion": profile.setup.motion_system,
            })
              .filter(([, v]) => v && v.trim())
              .map(([k, v]) => (
                <li key={k} className="flex justify-between gap-3">
                  <span className="text-muted-foreground">{k}</span>
                  <span className="text-right font-medium">{v}</span>
                </li>
              ))}
          </ul>
        </IconButton>
      )}

      {b.listings && profile.listings?.length > 0 && (
        <IconButton icon={Tag} label="Listings" testId="forum-icon-listings">
          <ul className="space-y-2">
            {profile.listings.slice(0, 6).map((l) => (
              <li key={l.id}>
                <Link to={`/marketplace/${l.id}`} className="flex items-center gap-2 rounded-md p-1 hover:bg-secondary">
                  {l.image ? <img src={l.image} alt="" className="h-8 w-8 rounded object-cover" /> : <div className="h-8 w-8 rounded bg-secondary" />}
                  <span className="flex-1 truncate text-sm">{l.title}</span>
                  <span className="text-sm font-semibold text-primary">{money(l.price, l.currency)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </IconButton>
      )}

      {b.simhub && profile.simhub?.length > 0 && (
        <IconButton icon={Gauge} label="Public SimHub Sessions" testId="forum-icon-simhub">
          <ul className="space-y-1.5">
            {profile.simhub.slice(0, 8).map((s) => (
              <li key={s.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="truncate">{s.track}</span>
                {s.game && <span className="shrink-0 text-xs text-muted-foreground">{s.game}</span>}
              </li>
            ))}
          </ul>
        </IconButton>
      )}

      {b.sponsors && profile.sponsors?.length > 0 && (
        <IconButton icon={Handshake} label="Sponsors" testId="forum-icon-sponsors">
          <div className="flex flex-wrap items-center gap-3">
            {profile.sponsors.map((s) => (
              <div key={s.id} className="flex flex-col items-center gap-1">
                {s.logo_url ? <img src={s.logo_url} alt={s.name} className="h-10 w-10 rounded object-contain" /> : null}
                <span className="text-[10px] text-muted-foreground">{s.name}</span>
              </div>
            ))}
          </div>
        </IconButton>
      )}

      {b.achievements && profile.achievements?.length > 0 && (
        <IconButton icon={Trophy} label="Achievements" testId="forum-icon-achievements">
          <ul className="space-y-1.5">
            {profile.achievements.slice(0, 12).map((a) => (
              <li key={a.id} className="flex items-center gap-2 text-sm">
                <Trophy className="h-3.5 w-3.5 text-primary" />
                <span>{a.label}</span>
              </li>
            ))}
          </ul>
        </IconButton>
      )}
    </div>
  );
}
